import React from "react"
import PropTypes from "prop-types"
import { Button, FormGroup, Label, Input, Row, Col } from "reactstrap"
import { LoadingButton } from "../../../../components/Common"
import { WEEKDAY_OPTIONS } from "../../../../constants/calendar";
import { usePlan } from "../../../../hooks/usePlan";
import "../Classes.css"

const ScheduleForm = ({
    values = {},
    handleChange,
    handleWeekdayToggle = () => { },
    handleStartTimeChange = () => { },
    handleDurationChange = () => { },
    activities = [],
    employees = [],
    areas = [],
    disabled = false,
    onSave = () => { },
    onDelete,
    saving = false,
}) => {
    const { planId } = usePlan();
    const isPersonal = planId === "personal";
    const currentWeekdays = values.weekdays || []

    // Helper to safely get value (converts null to empty string for controlled inputs)
    const safeValue = (key) => {
        const val = values[key];
        return val === null || val === undefined ? "" : val;
    };

    return (
        <div className="schedule-form">
            <Row>
                <Col lg={7}>
                    <div className="pe-lg-3">
                        <h5 className="form-section-header">
                            <i className="mdi mdi-card-text-outline text-primary"></i>
                            Informações da Turma
                        </h5>

                        <Row className="g-4">
                            <Col md={isPersonal ? "12" : "6"}>
                                <FormGroup className="mb-3">
                                    <Label className="fw-semibold text-muted font-size-12 text-uppercase mb-2">Atividade</Label>
                                    <Input
                                        type="select"
                                        name="idActivity"
                                        className="premium-input"
                                        value={safeValue("idActivity")}
                                        onChange={handleChange}
                                        disabled={Boolean(disabled)}
                                    >
                                        <option value="">Selecione a atividade...</option>
                                        {activities.map((activity) => (
                                            <option key={activity.id} value={activity.id}>
                                                {activity.name || activity.id}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>

                            {!isPersonal && (
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label className="fw-semibold text-muted font-size-12 text-uppercase mb-2">Colaborador Responsável</Label>
                                        <Input
                                            type="select"
                                            name="idEmployee"
                                            className="premium-input"
                                            value={safeValue("idEmployee")}
                                            onChange={handleChange}
                                            disabled={Boolean(disabled)}
                                        >
                                            <option value="">Selecione o colaborador...</option>
                                            {employees.map((emp) => (
                                                <option key={emp.id} value={emp.id}>
                                                    {emp.fullName || `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || emp.email || emp.id}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                            )}

                            <Col md="12">
                                <FormGroup className="mb-3">
                                    <Label className="fw-semibold text-muted font-size-12 text-uppercase mb-2">Área / Localização</Label>
                                    <Input
                                        type="select"
                                        name="idArea"
                                        className="premium-input"
                                        value={safeValue("idArea")}
                                        onChange={handleChange}
                                        disabled={Boolean(disabled)}
                                    >
                                        <option value="">Selecione o local...</option>
                                        {areas.map((area) => (
                                            <option key={area.id} value={area.id}>
                                                {area.name || area.id}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>

                            <Col md="12">
                                <FormGroup className="mb-0">
                                    <Label className="fw-semibold text-muted font-size-12 text-uppercase mb-3 d-flex align-items-center">
                                        <i className="mdi mdi-calendar-week me-2 text-primary font-size-18"></i>
                                        Dias da semana ativos
                                    </Label>

                                    <div className="weekday-selector-container">
                                        <Row className="g-2 text-center">
                                            {WEEKDAY_OPTIONS.map((option) => {
                                                const isActive = currentWeekdays.includes(option.value)
                                                return (
                                                    <Col key={option.value}>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleWeekdayToggle(option.value)}
                                                            disabled={Boolean(disabled)}
                                                            className={`weekday-btn ${isActive ? 'active' : ''}`}
                                                        >
                                                            {option.label.charAt(0)}
                                                        </button>
                                                        <span className={`weekday-label mt-2 ${isActive ? 'text-primary' : 'text-muted'}`}>
                                                            {option.label}
                                                        </span>
                                                    </Col>
                                                )
                                            })}
                                        </Row>
                                    </div>
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>
                </Col>

                <Col lg={5}>
                    <div className="ps-lg-4 border-start">
                        <h5 className="form-section-header">
                            <i className="mdi mdi-clock-outline text-primary"></i>
                            Horários e Vigência
                        </h5>

                        <Row className="g-4">
                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label className="fw-semibold text-muted font-size-12 text-uppercase mb-2">Hora Início</Label>
                                    <Input
                                        type="time"
                                        name="startTime"
                                        className="premium-input"
                                        value={safeValue("startTime")}
                                        onChange={handleStartTimeChange}
                                        disabled={Boolean(disabled)}
                                    />
                                </FormGroup>
                            </Col>

                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label className="fw-semibold text-muted font-size-12 text-uppercase mb-2">Duração (min)</Label>
                                    <Input
                                        type="number"
                                        name="durationMinutes"
                                        className="premium-input"
                                        placeholder="60"
                                        value={safeValue("durationMinutes")}
                                        onChange={handleDurationChange}
                                        disabled={Boolean(disabled)}
                                    />
                                </FormGroup>
                            </Col>

                            <Col md="12">
                                <FormGroup className="mb-3">
                                    <Label className="fw-semibold text-muted font-size-12 text-uppercase mb-2">Capacidade Máxima</Label>
                                    <Input
                                        type="number"
                                        name="maxCapacity"
                                        className="premium-input"
                                        placeholder="20"
                                        value={safeValue("maxCapacity")}
                                        onChange={handleChange}
                                        disabled={Boolean(disabled)}
                                    />
                                </FormGroup>
                            </Col>

                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label className="fw-semibold text-muted font-size-12 text-uppercase mb-2">Vigência Início</Label>
                                    <Input
                                        type="date"
                                        name="startDate"
                                        className="premium-input"
                                        value={safeValue("startDate")}
                                        onChange={handleChange}
                                        disabled={Boolean(disabled)}
                                    />
                                </FormGroup>
                            </Col>

                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label className="fw-semibold text-muted font-size-12 text-uppercase mb-2">
                                        Vigência Fim <span className="opacity-50 small">(Opcional)</span>
                                    </Label>
                                    <Input
                                        type="date"
                                        name="endDate"
                                        className="premium-input"
                                        value={safeValue("endDate")}
                                        onChange={handleChange}
                                        disabled={Boolean(disabled)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="operation-tip mt-4 shadow-sm">
                            <h6 className="font-size-13 fw-bold mb-2 text-primary">
                                <i className="mdi mdi-lightbulb-on-outline me-2"></i>
                                Dica de Operação
                            </h6>
                            <p className="mb-0 text-muted font-size-12">
                                Se não informar a data de fim, o sistema gerará sessões de forma recorrente.
                            </p>
                        </div>
                    </div>
                </Col>
            </Row>

            <div className="d-flex justify-content-between mt-5 pt-4 border-top">
                {onDelete && values.id ? (
                    <Button
                        color="danger"
                        outline
                        className="px-4 py-2 border-0 fw-semibold rounded-2"
                        onClick={onDelete}
                        disabled={Boolean(disabled) || saving}
                    >
                        <i className="mdi mdi-trash-can-outline me-2"></i>
                        Excluir Turma
                    </Button>
                ) : (
                    <div />
                )}

                <div className="d-flex gap-3">
                    {values.id && (
                        <Button
                            color="secondary"
                            outline
                            className="px-4 py-2 border-0 fw-semibold rounded-2"
                            onClick={() => onSave({ ...values, cancelEdit: true })}
                            disabled={disabled || saving}
                        >
                            Cancelar
                        </Button>
                    )}
                    <LoadingButton
                        color="primary"
                        className="px-5 py-2 fw-bold shadow-lg bg-gradient rounded-2"
                        style={{ minWidth: "160px" }}
                        onClick={onSave}
                        loading={saving}
                        loadingText="Processando..."
                        disabled={disabled || !onSave}
                    >
                        {values.id ? "Atualizar Dados" : "Criar Nova Turma"}
                    </LoadingButton>
                </div>
            </div>
        </div>
    )
}

ScheduleForm.propTypes = {
    values: PropTypes.object,
    errors: PropTypes.object,
    touched: PropTypes.object,
    handleChange: PropTypes.func,
    setFieldValue: PropTypes.func,
    handleWeekdayToggle: PropTypes.func,
    handleStartTimeChange: PropTypes.func,
    handleDurationChange: PropTypes.func,
    activities: PropTypes.array,
    employees: PropTypes.array,
    areas: PropTypes.array,
    disabled: PropTypes.bool,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    saving: PropTypes.bool,
}

export default ScheduleForm
